import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { iniciativasAPI, areasAPI, personasAPI, negociosAPI } from '../services/api';
import { Stepper, Step, StepLabel, Button as MUIButton, Box, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField } from '@mui/material';

const CrearIniciativa = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [areas, setAreas] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [iniciativas, setIniciativas] = useState([]);
  // Estados para combos relacionales de negocio
  const [lineasNegocio, setLineasNegocio] = useState([]);
  const [negociosFiltrados, setNegociosFiltrados] = useState([]);
  const [subnegociosFiltrados, setSubnegociosFiltrados] = useState([]);

  // Título y tipo fijo para iniciativas
  const tituloFormulario = 'Crear nuevo proyecto';
  const tipoPorDefecto = 'iniciativa';

  const [formData, setFormData] = useState({
    nombre_de_la_oportunidad: '',
    descripcion: '',
    tipo_de_iniciativa: 'iniciativa', // Proyecto por defecto
    innovacion: false, // Checkbox de innovación
    tipo_innovacion: '', // INNOCARD/INNOTRANS
    linea_de_negocio: '',
    negocio: '',
    subnegocio: '',
    negocio_estructura: '',
    contacto_de_negocio: '',
    tecnologia: '',
    area_ejecucion: '',
    responsable_comercial_id: '', // Jefe de proyecto
    lider_tecnico_id: '',
    desarrolladores: [], // Array para múltiples desarrolladores
    // Fechas
    fecha_de_inicio_planificada: '',
    fecha_de_inicio_real: '',
    fecha_de_fin_planificada: '',
    fecha_de_fin_real: '',
    fecha_de_aprobacion_propuesta_firmada: '',
    // Costes
    coste_de_desarrollo_estimado: '',
    importe_total_cliente: '',
    enlace_al_documento_ppt_sharepoint: '',
    enlace_a_la_pagina_de_confluence: '',
    padre: ''
  });

  useEffect(() => {
    cargarDatosReferencia();
  }, []);

  // Efecto para procesar las líneas de negocio únicas
  useEffect(() => {
    if (negocios && negocios.length > 0) {
      const lineasUnicas = [...new Set(negocios.map(n => n.linea_de_negocio))].sort();
      setLineasNegocio(lineasUnicas);
    }
  }, [negocios]);

  // Efecto para obtener todos los negocios únicos (sin filtrar)
  useEffect(() => {
    if (negocios && negocios.length > 0) {
      const negociosUnicos = [...new Set(negocios.map(n => n.negocio))].sort();
      setNegociosFiltrados(negociosUnicos);
    }
  }, [negocios]);

  // Efecto para obtener todos los subnegocios únicos (sin filtrar)
  useEffect(() => {
    if (negocios && negocios.length > 0) {
      const subnegociosUnicos = negocios
        .map(n => ({ id: n.id, subnegocio: n.subnegocio, negocio: n.negocio, linea_de_negocio: n.linea_de_negocio }))
        .sort((a, b) => a.subnegocio.localeCompare(b.subnegocio));
      setSubnegociosFiltrados(subnegociosUnicos);
    }
  }, [negocios]);

  const cargarDatosReferencia = async () => {
    try {
      const [areasResponse, personasResponse, negociosResponse, iniciativasResponse] = await Promise.all([
        areasAPI.getAll(),
        personasAPI.getAll(),
        negociosAPI.getAll(),
        iniciativasAPI.getAll()
      ]);
      setAreas(Array.isArray(areasResponse.data) ? areasResponse.data : areasResponse.data?.results || []);
      setPersonas(Array.isArray(personasResponse.data) ? personasResponse.data : personasResponse.data?.results || []);
      setNegocios(Array.isArray(negociosResponse.data) ? negociosResponse.data : negociosResponse.data?.results || []);
      setIniciativas(Array.isArray(iniciativasResponse.data) ? iniciativasResponse.data : iniciativasResponse.data?.results || []);
    } catch (err) {
      setError('Error al cargar los datos de referencia');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Manejar checkbox de innovación - si se desmarca, limpiar tipo_innovacion
    if (name === 'innovacion') {
      setFormData(prev => ({
        ...prev,
        innovacion: checked,
        tipo_innovacion: checked ? prev.tipo_innovacion : '' // Limpiar si se desmarca
      }));
      return;
    }

    // Manejar campos de costes con formateo automático
    if (name === 'coste_de_desarrollo_estimado' || name === 'importe_total_cliente') {
      const valorFormateado = formatearNumeroEspanolTiempoReal(value);
      setFormData(prev => ({
        ...prev,
        [name]: valorFormateado
      }));
      return;
    }

    // Manejar combos relacionales de negocio
    if (name === 'subnegocio') {
      // Encontrar el negocio completo por subnegocio
      const negocioCompleto = negocios.find(n => n.subnegocio === value);

      if (negocioCompleto) {
        setFormData(prev => ({
          ...prev,
          subnegocio: value,
          negocio: negocioCompleto.negocio,
          linea_de_negocio: negocioCompleto.linea_de_negocio,
          negocio_estructura: negocioCompleto.id
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          subnegocio: value,
          negocio_estructura: ''
        }));
      }
      return;
    }

    if (name === 'negocio') {
      // Encontrar la línea de negocio por negocio
      const negocioCompleto = negocios.find(n => n.negocio === value);

      if (negocioCompleto) {
        setFormData(prev => ({
          ...prev,
          negocio: value,
          linea_de_negocio: negocioCompleto.linea_de_negocio,
          subnegocio: '', // Limpiar subnegocio al cambiar negocio
          negocio_estructura: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          negocio: value,
          subnegocio: '',
          negocio_estructura: ''
        }));
      }
      return;
    }

    if (name === 'linea_de_negocio') {
      // Al cambiar línea de negocio, limpiar negocio y subnegocio
      setFormData(prev => ({
        ...prev,
        linea_de_negocio: value,
        negocio: '',
        subnegocio: '',
        negocio_estructura: ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Función específica para manejar desarrolladores múltiples
  const handleDesarrolladoresChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      desarrolladores: typeof value === 'string' ? value.split(',') : value
    }));
  };

  // Función para convertir formato español (1.000,50) a formato inglés (1000.50)
  const convertirFormatoEspanolAIngles = (valor) => {
    if (!valor || valor === '') return null;
    // Remover puntos (separadores de miles) y reemplazar coma por punto
    return parseFloat(valor.replace(/\./g, '').replace(',', '.')) || null;
  };

  // Función para formatear número en formato español en tiempo real
  const formatearNumeroEspanolTiempoReal = (valor) => {
    if (!valor || valor === '') return '';

    // Remover todo excepto números y coma
    let limpio = valor.replace(/[^0-9,]/g, '');

    // Dividir en parte entera y decimal
    let partes = limpio.split(',');
    let parteEntera = partes[0];
    let parteDecimal = partes[1];

    // Formatear parte entera con puntos cada 3 dígitos
    if (parteEntera.length > 3) {
      parteEntera = parteEntera.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    // Reconstruir el número
    let resultado = parteEntera;
    if (partes.length > 1) {
      // Limitar decimales a 2 dígitos
      if (parteDecimal) {
        parteDecimal = parteDecimal.substring(0, 2);
      }
      resultado += ',' + (parteDecimal || '');
    }

    return resultado;
  };

  // Convierte los errores de validación de la API (DRF) a mensajes legibles en español
  const parsearErroresAPI = (err) => {
    const etiquetas = {
      nombre_de_la_oportunidad: 'Nombre del proyecto',
      descripcion: 'Descripción',
      tipo_de_iniciativa: 'Tipo',
      negocio_estructura: 'Subnegocio / Negocio estructura',
      contacto_de_negocio: 'Contacto de negocio',
      tecnologia: 'Tecnología',
      area_ejecucion: 'Área de ejecución',
      estado: 'Estado',
      fecha_de_inicio_planificada: 'Fecha de inicio planificada',
      fecha_de_inicio_real: 'Fecha de inicio real',
      fecha_de_fin_planificada: 'Fecha de fin planificada',
      fecha_de_fin_real: 'Fecha de fin real',
      fecha_de_aprobacion_propuesta_firmada: 'Fecha de aprobación propuesta firmada',
      coste_de_desarrollo_estimado: 'Coste de desarrollo estimado',
      importe_total_cliente: 'Importe total cliente',
      enlace_al_documento_ppt_sharepoint: 'Enlace PPT SharePoint',
      enlace_a_la_pagina_de_confluence: 'Enlace Confluence',
      padre: 'Iniciativa padre',
      innovacion: 'Innovación',
      tipo_innovacion: 'Tipo de innovación',
      responsable_comercial_id: 'Responsable DN',
      lider_tecnico_id: 'Líder técnico',
      desarrolladores: 'Desarrolladores',
      año: 'Año',
    };
    const data = err?.response?.data;
    if (!data || typeof data !== 'object') {
      return 'Error al guardar. Por favor, revise los datos e intente nuevamente.';
    }
    const mensajes = [];
    Object.entries(data).forEach(([campo, errores]) => {
      const etiqueta = etiquetas[campo] || campo;
      const lista = Array.isArray(errores) ? errores : [errores];
      lista.forEach(msg => {
        const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
        if (/blank|null|vacío|required|obligatorio/i.test(msgStr)) {
          mensajes.push(`El campo "${etiqueta}" es obligatorio y no puede estar vacío.`);
        } else {
          mensajes.push(`${etiqueta}: ${msgStr}`);
        }
      });
    });
    return mensajes.length > 0
      ? mensajes.join('\n')
      : 'Error al guardar. Por favor, revise los datos e intente nuevamente.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const dataToSend = {
        ...formData,
        estado: 'planificada', // Estado por defecto
        año: new Date().getFullYear(), // Año automático
        innovacion: formData.innovacion, // Campo de innovación
        tipo_innovacion: formData.innovacion ? formData.tipo_innovacion || null : null, // Solo si es innovación
        // Fechas
        fecha_de_inicio_planificada: formData.fecha_de_inicio_planificada || null,
        fecha_de_inicio_real: formData.fecha_de_inicio_real || null,
        fecha_de_fin_planificada: formData.fecha_de_fin_planificada || null,
        fecha_de_fin_real: formData.fecha_de_fin_real || null,
        fecha_de_aprobacion_propuesta_firmada: formData.fecha_de_aprobacion_propuesta_firmada || null,
        // Costes (convertir formato español a inglés)
        coste_de_desarrollo_estimado: convertirFormatoEspanolAIngles(formData.coste_de_desarrollo_estimado),
        importe_total_cliente: convertirFormatoEspanolAIngles(formData.importe_total_cliente),
        // Otros campos
        tecnologia: formData.tecnologia || null,
        area_ejecucion: formData.area_ejecucion || null,
        negocio_estructura: formData.negocio_estructura || null,
        padre: formData.padre || null
      };
      const response = await iniciativasAPI.create(dataToSend);
      if (formData.responsable_comercial_id) {
        await iniciativasAPI.agregarPersona(response.data.id, {
          persona_id: formData.responsable_comercial_id,
          rol: 'responsable_comercial'
        });
      }
      if (formData.lider_tecnico_id) {
        await iniciativasAPI.agregarPersona(response.data.id, {
          persona_id: formData.lider_tecnico_id,
          rol: 'lider_tecnico'
        });
      }
      // Agregar desarrolladores múltiples
      if (formData.desarrolladores && formData.desarrolladores.length > 0) {
        for (const desarrolladorId of formData.desarrolladores) {
          if (desarrolladorId) {
            await iniciativasAPI.agregarPersona(response.data.id, {
              persona_id: desarrolladorId,
              rol: 'desarrollador'
            });
          }
        }
      }
      setSuccess(true);
      setTimeout(() => {
        navigate('/operaciones');
      }, 2000);
    } catch (err) {
      setError(parsearErroresAPI(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/operaciones');
  };

  const steps = [
    'Información Básica',
    'Fechas y Finanzas',
    'Enlaces'
  ];
  const [activeStep, setActiveStep] = useState(0);

  // Copiar renderStepContent de CrearOportunidad.js
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="row g-4">
            {/* Bloque 1: Información Básica */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">📋 Definición</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Nombre de la oportunidad */}
                    <div className="col-12">
                      <label className="form-label">Nombre del proyecto *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="nombre_de_la_oportunidad"
                        value={formData.nombre_de_la_oportunidad}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    {/* Descripción */}
                    <div className="col-12">
                      <label className="form-label">Descripción</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        name="descripcion"
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    {/* Iniciativa Padre */}
                    <div className="col-12">
                      <label className="form-label">Iniciativa Padre (opcional)</label>
                      <Autocomplete
                        options={[
                          { id: '', nombre_de_la_oportunidad: 'Ninguna (Iniciativa principal)', tipo_de_iniciativa: '' },
                          ...iniciativas
                            .filter(i => i.nombre_de_la_oportunidad)
                            .sort((a, b) => {
                              const nombreA = a.nombre_de_la_oportunidad || '';
                              const nombreB = b.nombre_de_la_oportunidad || '';
                              return nombreA.localeCompare(nombreB);
                            })
                        ]}
                        getOptionLabel={(option) => {
                          if (!option.id) return option.nombre_de_la_oportunidad;
                          return `${option.nombre_de_la_oportunidad}${option.tipo_de_iniciativa ? ` [${option.tipo_de_iniciativa}]` : ''}`;
                        }}
                        value={iniciativas.find(i => i.id === formData.padre) || { id: '', nombre_de_la_oportunidad: 'Ninguna (Iniciativa principal)', tipo_de_iniciativa: '' }}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({
                            ...prev,
                            padre: newValue ? newValue.id : ''
                          }));
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar iniciativa..."
                            variant="outlined"
                            size="small"
                          />
                        )}
                        noOptionsText="No se encontraron iniciativas"
                        clearText="Limpiar"
                        openText="Abrir"
                        closeText="Cerrar"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: '4px',
                          }
                        }}
                      />
                      <div className="form-text">Si esta iniciativa depende de otra, selecciónela aquí. ({iniciativas.length} iniciativas disponibles)</div>
                    </div>
                    {/* Tipo de Iniciativa */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Tipo *</label>
                      <select
                        className="form-control"
                        name="tipo_de_iniciativa"
                        value={formData.tipo_de_iniciativa}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="iniciativa">Iniciativa</option>
                        <option value="servicio">Servicio</option>
                        <option value="generica">Genérica</option>
                        <option value="mantenimiento">Mantenimiento</option>
                        <option value="programa">Programa</option>
                      </select>
                    </div>
                    {/* Innovación */}
                    <div className="col-lg-6 col-md-6">
                      <div className="d-flex align-items-center" style={{ height: '100%', paddingTop: '32px' }}>
                        <label className="form-check-label mb-1 me-2 ms-4" htmlFor="innovacionSwitch">
                          Innovación
                        </label>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="innovacion"
                            checked={formData.innovacion}
                            onChange={handleInputChange}
                            id="innovacionSwitch"
                          />

                        </div>
                      </div>
                    </div>
                    {/* Tipo de Innovación - solo si está marcado como innovación */}
                    {formData.innovacion && (
                      <div className="col-lg-6 col-md-6">
                        <label className="form-label">Tipo de Innovación</label>
                        <select
                          className="form-control"
                          name="tipo_innovacion"
                          value={formData.tipo_innovacion}
                          onChange={handleInputChange}
                        >
                          <option value="">No especificado</option>
                          <option value="innocard">Innocard</option>
                          <option value="innotrans">Innotrans</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bloque 2: Negocio */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">🏢 Información de Negocio</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Línea de negocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Línea de negocio *</label>
                      <select
                        className="form-control"
                        name="linea_de_negocio"
                        value={formData.linea_de_negocio}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Seleccionar línea...</option>
                        {lineasNegocio.map(linea => (
                          <option key={linea} value={linea}>{linea}</option>
                        ))}
                      </select>
                    </div>
                    {/* Negocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Negocio *</label>
                      <select
                        className="form-control"
                        name="negocio"
                        value={formData.negocio}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Seleccionar negocio...</option>
                        {negociosFiltrados.map(negocio => (
                          <option key={negocio} value={negocio}>{negocio}</option>
                        ))}
                      </select>
                    </div>
                    {/* Subnegocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Subnegocio *</label>
                      <select
                        className="form-control"
                        name="subnegocio"
                        value={formData.subnegocio}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Seleccionar subnegocio...</option>
                        {subnegociosFiltrados.map(item => (
                          <option key={item.id} value={item.subnegocio}>{item.subnegocio}</option>
                        ))}
                      </select>
                    </div>
                    {/* Contacto de negocio */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Contacto de negocio</label>
                      <input
                        type="text"
                        className="form-control"
                        name="contacto_de_negocio"
                        value={formData.contacto_de_negocio}
                        onChange={handleInputChange}
                        placeholder="Nombre del contacto"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bloque 3: Digital Hub */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">💻 Digital Hub</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Área de conocimiento */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Área de conocimiento</label>
                      <select
                        className="form-control"
                        name="tecnologia"
                        value={formData.tecnologia}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar área...</option>
                        {Array.isArray(areas) && areas.map(area => (
                          <option key={area.id} value={area.id}>{area.nombre}</option>
                        ))}
                      </select>
                    </div>
                    {/* Área de ejecución */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Área de ejecución</label>
                      <select
                        className="form-control"
                        name="area_ejecucion"
                        value={formData.area_ejecucion}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar área...</option>
                        {Array.isArray(areas) && areas.map(area => (
                          <option key={area.id} value={area.id}>{area.nombre}</option>
                        ))}
                      </select>
                    </div>
                    {/* Jefe de proyecto */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Jefe de proyecto</label>
                      <select
                        className="form-control"
                        name="responsable_comercial_id"
                        value={formData.responsable_comercial_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar persona...</option>
                        {Array.isArray(personas) && personas.map(persona => (
                          <option key={persona.id} value={persona.id}>
                            {persona.nombre} {persona.apellido}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Líder técnico */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Líder técnico</label>
                      <select
                        className="form-control"
                        name="lider_tecnico_id"
                        value={formData.lider_tecnico_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar persona...</option>
                        {Array.isArray(personas) && personas.map(persona => (
                          <option key={persona.id} value={persona.id}>
                            {persona.nombre} {persona.apellido}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Desarrolladores */}
                    <div className="col-12">
                      <label className="form-label">Desarrolladores</label>
                      <FormControl fullWidth size="small">
                        <InputLabel id="desarrolladores-label">Seleccionar desarrolladores</InputLabel>
                        <Select
                          labelId="desarrolladores-label"
                          name="desarrolladores"
                          multiple
                          value={formData.desarrolladores}
                          onChange={handleDesarrolladoresChange}
                          label="Seleccionar desarrolladores"
                          className="bg-white"
                        >
                          {Array.isArray(personas) && personas.map(persona => (
                            <MenuItem key={persona.id} value={persona.id}>
                              {persona.nombre} {persona.apellido}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="row g-4">
            {/* Bloque 1: Fechas */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">📅 Fechas</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Fecha de inicio planificada */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Fecha de inicio planificada</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_inicio_planificada"
                        value={formData.fecha_de_inicio_planificada}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* Fecha de inicio */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Fecha de inicio</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_inicio_real"
                        value={formData.fecha_de_inicio_real}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* Fecha de fin */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Fecha de fin</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_fin_planificada"
                        value={formData.fecha_de_fin_planificada}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* Fecha de fin real */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Fecha de fin real</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_fin_real"
                        value={formData.fecha_de_fin_real}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* Fecha de aprobación/propuesta firmada */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Fecha de aprobación/propuesta firmada</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_aprobacion_propuesta_firmada"
                        value={formData.fecha_de_aprobacion_propuesta_firmada}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bloque 2: Costes */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">💰 Costes</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Coste estimado de desarrollo */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Coste estimado de desarrollo (€)</label>
                      <input
                        type="text"
                        className="form-control"
                        name="coste_de_desarrollo_estimado"
                        value={formData.coste_de_desarrollo_estimado}
                        onChange={handleInputChange}
                        placeholder="0,00"
                        pattern="[0-9]{1,3}(\.[0-9]{3})*(,[0-9]{1,2})?"
                      />
                    </div>
                    {/* Coste total cliente */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Coste total cliente (€)</label>
                      <input
                        type="text"
                        className="form-control"
                        name="importe_total_cliente"
                        value={formData.importe_total_cliente}
                        onChange={handleInputChange}
                        placeholder="0,00"
                        pattern="[0-9]{1,3}(\.[0-9]{3})*(,[0-9]{1,2})?"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="row g-3">
            {/* Enlace al documento PPT */}
            <div className="col-lg-6 col-md-6">
              <label className="form-label">Enlace al documento (PPT - Sharepoint)</label>
              <input
                type="url"
                className="form-control"
                name="enlace_al_documento_ppt_sharepoint"
                value={formData.enlace_al_documento_ppt_sharepoint}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </div>
            {/* Enlace a Confluence */}
            <div className="col-lg-6 col-md-6">
              <label className="form-label">Enlace a la página de Confluence</label>
              <input
                type="url"
                className="form-control"
                name="enlace_a_la_pagina_de_confluence"
                value={formData.enlace_a_la_pagina_de_confluence}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="container mt-4 mb-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-success text-center" role="alert">
              <i className="fas fa-check-circle fa-3x mb-3"></i>
              <h4 className="alert-heading">¡Proyecto creado exitosamente!</h4>
              <p>El nuevo proyecto ha sido registrado en el sistema.</p>
              <hr />
              <p className="mb-0">Redirigiendo a la tabla de operaciones...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 mb-5">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">
                {/* <i className="fas fa-plus text-success me-2"></i> */}
                {tituloFormulario}
              </h2>
              <p className="text-muted mb-0">Complete el formulario para registrar un nuevo proyecto</p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleCancel}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Volver
            </button>
          </div>
          {/* Mensajes de error */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error.split('\n').map((linea, i) => (
                <div key={i}>{linea}</div>
              ))}
            </div>
          )}
          {/* Stepper y formulario paginado */}
          <div className="card">
            <div className="card-body">
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, idx) => (
                  <Step key={label}>
                    <StepLabel
                      style={{ cursor: idx <= activeStep ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (idx <= activeStep) setActiveStep(idx);
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              <form onSubmit={handleSubmit}>
                <Box sx={{ mt: 4 }}>{renderStepContent(activeStep)}</Box>
                <div className="d-flex justify-content-between mt-5">
                  <MUIButton
                    variant="outlined"
                    color="info"
                    onClick={() => setActiveStep((prev) => prev - 1)}
                    disabled={activeStep === 0}
                  >
                    Anterior
                  </MUIButton>
                  {activeStep < steps.length - 1 ? (
                    <MUIButton
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveStep((prev) => prev + 1);
                      }}
                    >
                      Siguiente
                    </MUIButton>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Creando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Crear Proyecto
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrearIniciativa;
